import {
    IModuleChartConfig,
    IMapSeriesConfigPayload,
    IModuleChartRequestPayload,
    IModulePlantChartConfigPayload,
    IModuleChartMetricsPayload,
    IModuleChartConfigPayload,
    IModuleChartMetrics,
    IModulePlantChartMultipleConfigPayload,
} from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { useApi, ApiPromise, apiAbortOptions } from '@/api/useApi.ts';
import { IJointConfigPayload } from '@/interfaces/powerMarkets/payloads/IJointConfigPayload.ts';
import { IJointDataPayload } from '@/interfaces/powerMarkets/payloads/IJointDataPayload.ts';
import { ISeriesList } from '@/interfaces/powerMarkets/series/ISeriesList.ts';
import { ITableDataConfig } from '@/interfaces/powerMarkets/table/ITableDataConfig.ts';
import { IChartModule } from '@/interfaces/powerMarkets/IModule.ts';
import { IHistoryOfForecastConfigRequest } from '@/interfaces/powerMarkets/charts/IHistoryOfForecastConfigRequest.ts';
import { IHistoryOfForecastDataReturn } from '@/interfaces/powerMarkets/charts/IHistoryOfForecastDataReturn.ts';
import { IGetHistoricForecastChartPayload, IHistoricForecastChart, IHistoricalHistoricForecastChart } from '@/interfaces/powerMarkets/charts/IHistoricForecastChart.ts';
import { IDateRangeCompositeNumbers } from '@/interfaces/general/IDateRange.ts';
import { ITreeMapPoint } from '@/interfaces/powerMarkets/charts/ITreeMapPoint.ts';

const apiCall = useApi();
const staticApiCall = useApi('static');
const seriesApiCall = useApi('series');
const calcApiCall = useApi('calc');
const mainApi = useApi('main');
const dynamicApiCall = useApi('dynamic');

function getChartToTableData (payload: Array<IModuleChartRequestPayload>, signal?: AbortSignal): ApiPromise<IGenericTableData> {
    return seriesApiCall.post<Array<IModuleChartRequestPayload>, IGenericTableData>({
        endpoint: '/ChartToTableApi',
        payload,
        ...apiAbortOptions(signal),
    });
}

function getChartToSummaryData (payload: IModuleChartMetricsPayload, signal?: AbortSignal): ApiPromise<Array<IModuleChartMetrics>> {
    return seriesApiCall.post<IModuleChartMetricsPayload, Array<IModuleChartMetrics>>({
        endpoint: '/ChartToSummaryApi',
        payload,
        ...apiAbortOptions(signal),
    });
}

function getChartToCumulativeData (payload: Array<IModuleChartRequestPayload>, signal?: AbortSignal): ApiPromise<ISeriesList> {
    return seriesApiCall.post<Array<IModuleChartRequestPayload>, ISeriesList>({
        endpoint: '/ChartToCumulativeApi',
        payload,
        ...apiAbortOptions(signal),
    });
}

async function getJointSerieshistoryOfForecastData (payload: IHistoryOfForecastConfigRequest): Promise<IHistoryOfForecastDataReturn> {
    const response = await mainApi.post<IHistoryOfForecastConfigRequest, IHistoryOfForecastDataReturn>({
        endpoint: '/JointSeries/historyOfForecast/data',
        payload,
    });

    return response.data;
}

async function getJointSerieshistoryOfForecastConfig (payload: IHistoryOfForecastConfigRequest): Promise<ITableDataConfig> {
    const response = await mainApi.post<IHistoryOfForecastConfigRequest, ITableDataConfig>({
        endpoint: '/JointSeries/historyOfForecast/config',
        payload,
    });

    return response.data;
}

async function getHistoricSeriesData (payload: Array<IModuleChartRequestPayload>): Promise<ISeriesList> {
    const response = await seriesApiCall.post<Array<IModuleChartRequestPayload>, ISeriesList>({ endpoint: '/HistoricSeriesApi', payload });
    return response.data;
}

async function getJointSeriesConfigFromSingleId (id: string, countryId?: string): Promise<ITableDataConfig> {
    const response = await staticApiCall.post<{ id: string; countryId?: string }, ITableDataConfig>({ endpoint: '/jointConfig/configFromSingleId', payload: { id, countryId } });
    return response.data;
}

async function getJointSeriesConfig (payload: Array<IChartModule>): Promise<Record<string, IModuleChartConfig | ITableDataConfig>> {
    const response = await staticApiCall.post<Array<IChartModule>, Record<string, IModuleChartConfig>>({
        endpoint: '/jointConfig/config',
        payload,
    });

    return response.data;
}

async function getJointSeriesConfigAncillary (seriesConfig: Array<IJointConfigPayload>): Promise<{ singleChart: ITableDataConfig }> {
    const response = await staticApiCall.post<Array<IJointConfigPayload>, { singleChart: ITableDataConfig }>({ endpoint: '/jointConfig/config', payload: seriesConfig });
    return response.data;
}

async function getJointSeriesData (payload: Array<IJointDataPayload>): Promise<ISeriesList> {
    const response = await apiCall.post<Array<IJointDataPayload>, ISeriesList>({ endpoint: '/jointConfig/data', payload });
    return response.data;
}

function getDynamicSeriesConfig (payload: Array<IModuleChartConfigPayload>): ApiPromise<Array<IModuleChartConfig>> {
    return staticApiCall.post<Array<IModuleChartConfigPayload>, Array<IModuleChartConfig>>({
        endpoint: '/dynamicchart/config',
        payload,
    });
}

async function getPlantChartSeriesConfig (payload: Array<IModulePlantChartConfigPayload | IModulePlantChartMultipleConfigPayload>): Promise<Array<IModuleChartConfig>> {
    const response = await staticApiCall.post<Array<IModulePlantChartConfigPayload | IModulePlantChartMultipleConfigPayload>, Array<IModuleChartConfig>>({
        endpoint: '/plantchart/config',
        payload,
    });

    return response.data;
}

async function getPlantChartCombinedConfig (payload: Array<IModulePlantChartConfigPayload>): Promise<Array<IModuleChartConfig>> {
    const response = await staticApiCall.post<Array<IModulePlantChartConfigPayload>, Array<IModuleChartConfig>>({
        endpoint: '/plantchart/combineConfig',
        payload,
    });

    return response.data;
}

function getChartData (payload: Array<IModuleChartRequestPayload>, endpoint: string, useCalc = false, useDynamic = false): ApiPromise<ISeriesList> {
    if (useCalc) {
        return calcApiCall.post<Array<IModuleChartRequestPayload>, ISeriesList>({
            endpoint,
            payload,
        });
    }

    if (useDynamic) {
        return dynamicApiCall.post<Array<IModuleChartRequestPayload>, ISeriesList>({
            endpoint,
            payload,
        });
    }

    return seriesApiCall.post<Array<IModuleChartRequestPayload>, ISeriesList>({
        endpoint,
        payload,
    });
}

function getMapSeriesConfig (payload: Array<IMapSeriesConfigPayload>): ApiPromise<{ 'europe': ITableDataConfig}> {
    return mainApi.post<Array<IMapSeriesConfigPayload>, { 'europe': ITableDataConfig}>({
        endpoint: '/entsoemapseries/config',
        payload,
    });
}

function getForecastHistoryEndpoint (
    payload: IGetHistoricForecastChartPayload['series'],
    signal?: AbortSignal,
): ApiPromise<string> {
    return staticApiCall.post<IGetHistoricForecastChartPayload['series'], string>({
        endpoint: '/forecastHistory/endpoint',
        payload,
        ...apiAbortOptions(signal),
    });
}

async function getHistoricForecastChartConfig (
    payload: IGetHistoricForecastChartPayload,
    signal?: AbortSignal,
): ApiPromise<ITableDataConfig> {
    const endpoint = await getForecastHistoryEndpoint(payload.series, signal);
    return useApi('static', endpoint.data).post<IGetHistoricForecastChartPayload, ITableDataConfig>({
        endpoint: '/historyOfForecast/config/',
        payload,
        ...apiAbortOptions(signal),
    });
}
async function getHistoricForecastChartData (
    payload: IGetHistoricForecastChartPayload,
    signal?: AbortSignal,
): ApiPromise<IHistoricForecastChart> {
    const endpoint = await getForecastHistoryEndpoint(payload.series, signal);
    return useApi('static', endpoint.data).post<IGetHistoricForecastChartPayload, IHistoricForecastChart>({
        endpoint: '/historyOfForecast/data/',
        payload,
        ...apiAbortOptions(signal),
    });
}
async function getHistoricForecastChartHistoricData (
    payload: IGetHistoricForecastChartPayload,
    signal?: AbortSignal,
): ApiPromise<IHistoricalHistoricForecastChart> {
    const endpoint = await getForecastHistoryEndpoint(payload.series, signal);
    return useApi('static', endpoint.data).post<IGetHistoricForecastChartPayload, IHistoricalHistoricForecastChart>({
        endpoint: '/historyOfForecast/historicData/',
        payload,
        ...apiAbortOptions(signal),
    });
}
function updateCalcListening (calcRequests: Array<IModuleChartRequestPayload>, signal?: AbortSignal): ApiPromise<unknown> {
    return calcApiCall.post<Array<IModuleChartRequestPayload>, unknown>({
        endpoint: '/updateListening',
        payload: calcRequests,
        ...apiAbortOptions(signal),
    });
}

function getTreeMapData (payload: IDateRangeCompositeNumbers): ApiPromise<Array<ITreeMapPoint>> {
    return seriesApiCall.post<IDateRangeCompositeNumbers, Array<ITreeMapPoint>>({
        endpoint: '/NgesoTreemapApi',
        payload,
    });
}

function getSeriesSummary (payload: IGetSeriesSummaryPayload, signal?: AbortSignal): ApiPromise<IGetSeriesSummaryResponse> {
    return seriesApiCall.post<IGetSeriesSummaryPayload, IGetSeriesSummaryResponse>({
        endpoint: '/SeriesSummaryApi',
        payload,
        ...apiAbortOptions(signal),
    });
}
export interface IGetSeriesSummaryResponse {
    average: number;
    percentiles: number[];
}

export interface IGetSeriesSummaryPayload {
    id: string;
    granularity: string;
    type: string;
}

interface IGetCurrencyScalarsPayload {
    dayFrom: string;
    dayTo: string;
    currencyFrom: string;
    currencyTo: string;
}

interface IGetCurrencyScalarsResponse {
    scalars: Record<string, number>;
}

function getCurrencyScalars (payload: IGetCurrencyScalarsPayload, signal?: AbortSignal): ApiPromise<IGetCurrencyScalarsResponse> {
    return seriesApiCall.post<IGetCurrencyScalarsPayload, IGetCurrencyScalarsResponse>({
        endpoint: '/CurrencyApi',
        payload,
        ...apiAbortOptions(signal),
    });
}

export const chartApi = {
    getChartData,
    getChartToCumulativeData,
    getHistoricSeriesData,
    getChartToSummaryData,
    getChartToTableData,
    getJointSeriesConfigFromSingleId,
    getDynamicSeriesConfig,
    getJointSerieshistoryOfForecastData,
    getJointSerieshistoryOfForecastConfig,
    getJointSeriesData,
    getJointSeriesConfigAncillary,
    getJointSeriesConfig,
    getMapSeriesConfig,
    getPlantChartSeriesConfig,
    getPlantChartCombinedConfig,
    getHistoricForecastChartConfig,
    getHistoricForecastChartData,
    getHistoricForecastChartHistoricData,
    updateCalcListening,
    getTreeMapData,
    getCurrencyScalars,
    getSeriesSummary,
};
